import * as THREE from "three";
import { POLYGONS } from "../Global/variables";
import { sceneManager } from "../SetUps/SceneManager";

export const IRRADIANCE_OVERLAY_NAME = "IRRADIANCE_SPRITE";
const IRRADIANCE_OVERLAY_RENDER_ORDER = 2;

const COLOR_STOPS = [
  { stop: 1.0, color: new THREE.Color(0xfff75a) },
  { stop: 0.95, color: new THREE.Color(0xffea3a) },
  { stop: 0.9, color: new THREE.Color(0xffd11a) },
  { stop: 0.85, color: new THREE.Color(0xffb300) },
  { stop: 0.8, color: new THREE.Color(0xff9800) },
  { stop: 0.75, color: new THREE.Color(0xff7b00) },
  { stop: 0.7, color: new THREE.Color(0xff6300) },
  { stop: 0.6, color: new THREE.Color(0xff4a1a) },
  { stop: 0.5, color: new THREE.Color(0xf03a1f) },
  { stop: 0.4, color: new THREE.Color(0xdb2a1f) },
  { stop: 0.3, color: new THREE.Color(0xc01d1d) },
  { stop: 0.2, color: new THREE.Color(0x9e1418) },
  { stop: 0.0, color: new THREE.Color(0x750d12) },
];

const overlayInterpolators = new WeakMap();

const hoverState = {
  objects: [],
  domElement: null,
  moveHandler: null,
  leaveHandler: null,
  pendingEvent: null,
  rafId: null,
  raycaster: new THREE.Raycaster(),
  pointerNdc: new THREE.Vector2(),
  tooltip: null,
};

function clamp01(value) {
  if (!Number.isFinite(value)) return 0;
  return Math.max(0, Math.min(1, value));
}

function getColorFromASA(value) {
  const t = clamp01(value);

  for (let i = 0; i < COLOR_STOPS.length - 1; i++) {
    const left = COLOR_STOPS[i];
    const right = COLOR_STOPS[i + 1];
    if (t <= left.stop && t >= right.stop) {
      const localT = (t - right.stop) / (left.stop - right.stop);
      return left.color.clone().lerp(right.color, localT);
    }
  }

  return new THREE.Color(0x000000);
}

function ensureTooltip() {
  if (hoverState.tooltip || typeof document === "undefined") return;

  const el = document.createElement("div");
  el.style.position = "fixed";
  el.style.left = "0px";
  el.style.top = "0px";
  el.style.padding = "6px 8px";
  el.style.borderRadius = "6px";
  el.style.background = "rgba(0,0,0,0.8)";
  el.style.color = "#fff";
  el.style.fontSize = "12px";
  el.style.fontWeight = "600";
  el.style.pointerEvents = "none";
  el.style.zIndex = "9999";
  el.style.display = "none";
  el.style.whiteSpace = "nowrap";
  el.id = "irradiance-hover-tooltip";

  document.body.appendChild(el);
  hoverState.tooltip = el;
}

function hideTooltip() {
  if (hoverState.tooltip) {
    hoverState.tooltip.style.display = "none";
  }
}

function showTooltip(clientX, clientY, text) {
  ensureTooltip();
  if (!hoverState.tooltip) return;

  hoverState.tooltip.textContent = text;
  hoverState.tooltip.style.left = `${clientX + 12}px`;
  hoverState.tooltip.style.top = `${clientY + 12}px`;
  hoverState.tooltip.style.display = "block";
}

function pruneHoverObjects() {
  hoverState.objects = hoverState.objects.filter(
    (obj) => isIrradianceOverlay(obj) && obj.parent
  );
}

function processHoverEvent(event) {
  pruneHoverObjects();
  if (!hoverState.objects.length) {
    hideTooltip();
    return;
  }

  const renderer = sceneManager?.renderer;
  const camera = sceneManager?.camera;
  const dom = hoverState.domElement || renderer?.domElement;
  if (!renderer || !camera || !dom) {
    hideTooltip();
    return;
  }

  const visibleObjects = hoverState.objects.filter((obj) => obj.visible !== false);
  if (!visibleObjects.length) {
    hideTooltip();
    return;
  }

  const rect = dom.getBoundingClientRect();
  if (rect.width <= 0 || rect.height <= 0) {
    hideTooltip();
    return;
  }

  hoverState.pointerNdc.set(
    ((event.clientX - rect.left) / rect.width) * 2 - 1,
    -((event.clientY - rect.top) / rect.height) * 2 + 1
  );

  hoverState.raycaster.setFromCamera(hoverState.pointerNdc, camera);
  hoverState.raycaster.layers.set(1);
  const hits = hoverState.raycaster.intersectObjects(visibleObjects, false);
  const hit = hits.find((entry) => overlayInterpolators.has(entry.object));

  if (!hit) {
    hideTooltip();
    return;
  }

  const interpolator = overlayInterpolators.get(hit.object);
  const asaValue = clamp01(interpolator?.(hit.point));
  showTooltip(event.clientX, event.clientY, `Irradiance ${(
    asaValue * 100
  ).toFixed(1)}%`);
}

function detachHoverHandlers() {
  if (hoverState.domElement && hoverState.moveHandler) {
    hoverState.domElement.removeEventListener("pointermove", hoverState.moveHandler);
  }
  if (hoverState.domElement && hoverState.leaveHandler) {
    hoverState.domElement.removeEventListener("pointerleave", hoverState.leaveHandler);
  }

  hoverState.domElement = null;
  hoverState.moveHandler = null;
  hoverState.leaveHandler = null;
  hoverState.pendingEvent = null;

  if (hoverState.rafId) {
    cancelAnimationFrame(hoverState.rafId);
    hoverState.rafId = null;
  }

  hideTooltip();
}

function attachHoverHandlers() {
  const dom = sceneManager?.renderer?.domElement;
  if (!dom) return;

  if (hoverState.domElement === dom && hoverState.moveHandler && hoverState.leaveHandler) {
    return;
  }

  detachHoverHandlers();
  hoverState.domElement = dom;

  hoverState.moveHandler = (event) => {
    hoverState.pendingEvent = event;
    if (hoverState.rafId) return;

    hoverState.rafId = requestAnimationFrame(() => {
      hoverState.rafId = null;
      const evt = hoverState.pendingEvent;
      hoverState.pendingEvent = null;
      if (evt) processHoverEvent(evt);
    });
  };

  hoverState.leaveHandler = () => hideTooltip();
  dom.addEventListener("pointermove", hoverState.moveHandler);
  dom.addEventListener("pointerleave", hoverState.leaveHandler);
}

function setHoverTargets(objects) {
  hoverState.objects = Array.isArray(objects) ? [...objects] : [];
  if (hoverState.objects.length > 0) attachHoverHandlers();
  else detachHoverHandlers();
}

function toVector3(value) {
  if (value instanceof THREE.Vector3) return value.clone();
  return new THREE.Vector3(
    Number(value?.x ?? 0),
    Number(value?.y ?? 0),
    Number(value?.z ?? 0)
  );
}

function createASAInterpolator(rawSamples) {
  const samples = (rawSamples || [])
    .map((sample) => ({
      point: toVector3(sample?.coordinates),
      asa: clamp01(Number(sample?.asa ?? 0)),
    }))
    .filter(
      (sample) =>
        Number.isFinite(sample.point.x) &&
        Number.isFinite(sample.point.y) &&
        Number.isFinite(sample.point.z)
    );

  const count = samples.length;
  if (!count) return () => 0;

  const xs = new Float32Array(count);
  const ys = new Float32Array(count);
  const zs = new Float32Array(count);
  const asas = new Float32Array(count);

  for (let i = 0; i < count; i++) {
    xs[i] = samples[i].point.x;
    ys[i] = samples[i].point.y;
    zs[i] = samples[i].point.z;
    asas[i] = samples[i].asa;
  }

  return (worldPoint) => {
    if (!worldPoint || count === 0) return 0;

    const nearestDist = [Infinity, Infinity, Infinity, Infinity];
    const nearestAsa = [0, 0, 0, 0];

    for (let i = 0; i < count; i++) {
      const dx = worldPoint.x - xs[i];
      const dy = worldPoint.y - ys[i];
      const dz = worldPoint.z - zs[i];
      const distSq = dx * dx + dy * dy + dz * dz;

      if (distSq < 1e-8) {
        return asas[i];
      }

      if (distSq >= nearestDist[3]) continue;

      let slot = 3;
      while (slot > 0 && distSq < nearestDist[slot - 1]) {
        nearestDist[slot] = nearestDist[slot - 1];
        nearestAsa[slot] = nearestAsa[slot - 1];
        slot -= 1;
      }
      nearestDist[slot] = distSq;
      nearestAsa[slot] = asas[i];
    }

    let weightedValue = 0;
    let weightSum = 0;

    for (let i = 0; i < 4; i++) {
      if (!Number.isFinite(nearestDist[i])) continue;
      const weight = 1 / (nearestDist[i] + 1e-6);
      weightedValue += nearestAsa[i] * weight;
      weightSum += weight;
    }

    return weightSum > 0 ? weightedValue / weightSum : 0;
  };
}

function getSubdivisionLevel(sampleCount) {
  if (!Number.isFinite(sampleCount) || sampleCount <= 0) return 2;
  return Math.max(2, Math.min(8, Math.round(Math.sqrt(sampleCount) / 2)));
}

function appendSubdividedTriangle(
  a,
  b,
  c,
  subdivisions,
  interpolateASA,
  overlayLift,
  outPositions,
  outColors,
  outIndices
) {
  const rowStarts = [];
  let vertexIndex = outPositions.length / 3;
  const samplePoint = new THREE.Vector3();

  for (let i = 0; i <= subdivisions; i++) {
    rowStarts[i] = vertexIndex;
    for (let j = 0; j <= subdivisions - i; j++) {
      const wB = i / subdivisions;
      const wC = j / subdivisions;
      const wA = 1 - wB - wC;

      const px = a.x * wA + b.x * wB + c.x * wC;
      const py = a.y * wA + b.y * wB + c.y * wC;
      const pz = a.z * wA + b.z * wB + c.z * wC;

      samplePoint.set(px, py, pz);
      const asa = clamp01(interpolateASA(samplePoint));
      const color = getColorFromASA(asa);

      outPositions.push(px + overlayLift.x, py + overlayLift.y, pz + overlayLift.z);
      outColors.push(color.r, color.g, color.b);
      vertexIndex += 1;
    }
  }

  for (let i = 0; i < subdivisions; i++) {
    const cols = subdivisions - i;
    for (let j = 0; j < cols; j++) {
      const v0 = rowStarts[i] + j;
      const v1 = rowStarts[i + 1] + j;
      const v2 = rowStarts[i] + j + 1;
      outIndices.push(v0, v1, v2);

      if (j < cols - 1) {
        const v3 = rowStarts[i + 1] + j + 1;
        outIndices.push(v1, v3, v2);
      }
    }
  }
}

function getSegmentPlane(segmentMesh, segment) {
  if (Array.isArray(segment?.edges) && segment.edges.length >= 3) {
    const points = segment.edges
      .map((edge) => edge?.point1)
      .filter(Boolean)
      .map((p) => new THREE.Vector3(Number(p.x), Number(p.y), Number(p.z)));

    if (points.length >= 3) {
      const anchor = points[0];
      for (let i = 1; i < points.length - 1; i++) {
        const b = points[i];
        const c = points[i + 1];
        const normal = new THREE.Vector3()
          .subVectors(b, anchor)
          .cross(new THREE.Vector3().subVectors(c, anchor));

        if (normal.lengthSq() > 1e-10) {
          normal.normalize();
          if (normal.z < 0) normal.negate();
          return new THREE.Plane().setFromNormalAndCoplanarPoint(normal, anchor);
        }
      }
    }
  }

  if (segmentMesh?.geometry) {
    segmentMesh.updateMatrixWorld(true);

    const g = segmentMesh.geometry.index
      ? segmentMesh.geometry.toNonIndexed()
      : segmentMesh.geometry;
    const shouldDispose = g !== segmentMesh.geometry;

    try {
      const pos = g.getAttribute("position");
      if (pos && pos.count >= 3) {
        const a = new THREE.Vector3()
          .fromBufferAttribute(pos, 0)
          .applyMatrix4(segmentMesh.matrixWorld);
        const b = new THREE.Vector3()
          .fromBufferAttribute(pos, 1)
          .applyMatrix4(segmentMesh.matrixWorld);
        const c = new THREE.Vector3()
          .fromBufferAttribute(pos, 2)
          .applyMatrix4(segmentMesh.matrixWorld);
        const normal = new THREE.Vector3()
          .subVectors(b, a)
          .cross(new THREE.Vector3().subVectors(c, a));

        if (normal.lengthSq() > 1e-10) {
          normal.normalize();
          if (normal.z < 0) normal.negate();
          return new THREE.Plane().setFromNormalAndCoplanarPoint(normal, a);
        }
      }
    } finally {
      if (shouldDispose) g.dispose();
    }
  }

  return new THREE.Plane(new THREE.Vector3(0, 0, 1), 0);
}

function getOverlayLift(segmentMesh, segment) {
  const normal = getSegmentPlane(segmentMesh, segment).normal.clone().normalize();
  const box = new THREE.Box3().setFromObject(segmentMesh);
  const size = new THREE.Vector3();
  box.getSize(size);
  const diag = size.length();

  // Small world-space lift to avoid coplanar depth artifacts.
  const liftDistance = Math.max(0.045, Math.min(0.22, diag * 0.0075));
  return normal.multiplyScalar(liftDistance);
}

function buildGradientGeometry(segmentMesh, segment, interpolateASA, sampleCount) {
  if (!segmentMesh?.geometry) return null;
  segmentMesh.updateMatrixWorld(true);

  const sourceGeometry = segmentMesh.geometry;
  const workingGeometry = sourceGeometry.index
    ? sourceGeometry.toNonIndexed()
    : sourceGeometry;
  const shouldDisposeWorking = workingGeometry !== sourceGeometry;

  try {
    const posAttr = workingGeometry.getAttribute("position");
    if (!posAttr || posAttr.count < 3) return null;

    const subdivisions = getSubdivisionLevel(sampleCount);
    const outPositions = [];
    const outColors = [];
    const outIndices = [];
    const segmentPlane = getSegmentPlane(segmentMesh, segment);
    const overlayLift = getOverlayLift(segmentMesh, segment);

    const a = new THREE.Vector3();
    const b = new THREE.Vector3();
    const c = new THREE.Vector3();
    const projectedA = new THREE.Vector3();
    const projectedB = new THREE.Vector3();
    const projectedC = new THREE.Vector3();

    for (let i = 0; i < posAttr.count; i += 3) {
      a.fromBufferAttribute(posAttr, i).applyMatrix4(segmentMesh.matrixWorld);
      b.fromBufferAttribute(posAttr, i + 1).applyMatrix4(segmentMesh.matrixWorld);
      c.fromBufferAttribute(posAttr, i + 2).applyMatrix4(segmentMesh.matrixWorld);

      segmentPlane.projectPoint(a, projectedA);
      segmentPlane.projectPoint(b, projectedB);
      segmentPlane.projectPoint(c, projectedC);

      appendSubdividedTriangle(
        projectedA,
        projectedB,
        projectedC,
        subdivisions,
        interpolateASA,
        overlayLift,
        outPositions,
        outColors,
        outIndices
      );
    }

    const geometry = new THREE.BufferGeometry();
    geometry.setAttribute("position", new THREE.Float32BufferAttribute(outPositions, 3));
    geometry.setAttribute("color", new THREE.Float32BufferAttribute(outColors, 3));
    geometry.setIndex(outIndices);
    geometry.computeBoundingSphere();

    return geometry;
  } finally {
    if (shouldDisposeWorking) workingGeometry.dispose();
  }
}

function createOverlayMaterial() {
  return new THREE.MeshBasicMaterial({
    vertexColors: true,
    side: THREE.DoubleSide,
    transparent: true,
    opacity: 0.9,
    // This material is rendered in its own depth pass. Writing depth lets
    // overlapping ASA planes resolve against each other correctly.
    depthWrite: true,
    polygonOffset: true,
    polygonOffsetFactor: -4,
    polygonOffsetUnits: -4,
  });
}

function groupBySegment(finalData) {
  const groups = new Map();

  (finalData || []).forEach((item) => {
    if (!item) return;

    const segId = item.segmentId ?? item.segmentID ?? null;
    let segIndex = null;
    if (Number.isInteger(item.segmentIndex)) {
      segIndex = item.segmentIndex;
    } else if (
      typeof item.segmentIndex === "string" &&
      item.segmentIndex.trim() !== ""
    ) {
      const parsed = Number(item.segmentIndex);
      segIndex = Number.isInteger(parsed) ? parsed : null;
    }
    const key = segId != null ? `id:${segId}` : `index:${segIndex}`;

    if (!groups.has(key)) {
      groups.set(key, {
        segmentId: segId,
        segmentIndex: segIndex,
        points: [],
      });
    }

    groups.get(key).points.push(item);
  });

  return Array.from(groups.values());
}

function resolveSegmentForGroup(group) {
  if (group?.segmentId != null) {
    const byId = POLYGONS.find((segment) => segment?.id === group.segmentId);
    if (byId?.shape) return byId;
  }

  if (Number.isInteger(group?.segmentIndex)) {
    const byIndex = POLYGONS[group.segmentIndex];
    if (byIndex?.shape) return byIndex;
  }

  return null;
}

function disposeMaterial(material) {
  const materials = Array.isArray(material) ? material : [material];

  materials.forEach((mat) => {
    if (!mat) return;

    const textureKeys = [
      "map",
      "alphaMap",
      "aoMap",
      "bumpMap",
      "normalMap",
      "displacementMap",
      "emissiveMap",
      "roughnessMap",
      "metalnessMap",
      "envMap",
    ];

    textureKeys.forEach((key) => {
      if (mat[key]?.dispose) mat[key].dispose();
    });

    if (mat.dispose) mat.dispose();
  });
}

export function isIrradianceOverlay(obj) {
  return Boolean(obj && obj.name === IRRADIANCE_OVERLAY_NAME);
}

// Render normal scene content first, then ASA with a fresh depth buffer. This
// lets ASA cover its source roof without competing with the roof's depth while
// preserving depth ordering between overlapping ASA planes (e.g. dormers).
export function renderSceneWithIrradianceOverlay(renderer, scene, camera) {
  if (!renderer || !scene || !camera) return;

  const originalMask = camera.layers.mask;
  const originalAutoClear = renderer.autoClear;

  try {
    camera.layers.set(0);
    renderer.autoClear = originalAutoClear;
    renderer.render(scene, camera);

    renderer.clearDepth();
    camera.layers.set(1);
    renderer.autoClear = false;
    renderer.render(scene, camera);
  } finally {
    camera.layers.mask = originalMask;
    renderer.autoClear = originalAutoClear;
  }
}

export function disposeIrradianceOverlay(obj) {
  if (!obj) return;

  overlayInterpolators.delete(obj);

  if (obj.parent) obj.parent.remove(obj);
  if (obj.geometry?.dispose) obj.geometry.dispose();
  disposeMaterial(obj.material);

  hoverState.objects = hoverState.objects.filter((entry) => entry !== obj);
  if (!hoverState.objects.length) detachHoverHandlers();
}

export function addASAtoScene(scene, finalData, options = {}) {
  const { enableHover = true } = options || {};
  if (!scene || !Array.isArray(finalData) || finalData.length === 0) {
    if (enableHover) setHoverTargets([]);
    return [];
  }

  const createdOverlays = [];
  const groups = groupBySegment(finalData);

  groups.forEach((group) => {
    const segment = resolveSegmentForGroup(group);
    const segmentMesh = segment?.shape;
    if (!segmentMesh?.geometry) return;

    const interpolator = createASAInterpolator(group.points);
    const geometry = buildGradientGeometry(
      segmentMesh,
      segment,
      interpolator,
      group.points.length
    );
    if (!geometry) return;

    const mesh = new THREE.Mesh(geometry, createOverlayMaterial());
    mesh.name = IRRADIANCE_OVERLAY_NAME;
    mesh.renderOrder = 3;
    mesh.layers.set(1);
    mesh.userData.segmentId = group.segmentId;
    mesh.userData.segmentIndex = group.segmentIndex;

    overlayInterpolators.set(mesh, interpolator);
    scene.add(mesh);
    createdOverlays.push(mesh);
  });

  if (enableHover) {
    setHoverTargets(createdOverlays);
  }
  return createdOverlays;
}
